import { useState } from 'react';
import { Search, UserPlus } from 'lucide-react';
import api from '../../services/api';
import { useToast } from '../../contexts/ToastContext';

/**
 * Putting somebody on the list who did not register themselves.
 *
 * People are added off the platform all the time — they paid in person, they
 * asked at a stall, they emailed. Without this, an organiser who knows someone
 * is coming has no way to say so.
 */

interface Found { _id: string; name: string; email: string; role: string }

export default function AddParticipant({ eventId, onAdded }: { eventId: string; onAdded?: () => void }) {
  const { success, error } = useToast();
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<Found[] | null>(null);
  const [busy, setBusy] = useState(false);

  const search = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/registrations/admin/searchable-users?q=${encodeURIComponent(term)}`);
      setResults(res.data.users || []);
    } catch (err: any) {
      error(err.response?.data?.message || 'Could not search');
    } finally { setBusy(false); }
  };

  const add = async (payload: Record<string, unknown>, who: string) => {
    setBusy(true);
    try {
      const res = await api.post(`/registrations/admin/add-participant/${eventId}`, payload);
      success(res.data.message);
      setResults(null); setTerm('');
      onAdded?.();
    } catch (err: any) {
      error(err.response?.data?.message || `Could not add ${who}`);
    } finally { setBusy(false); }
  };

  const looksLikeEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(term.trim());

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5">
      <h3 className="mb-1 flex items-center gap-2 text-lg font-semibold">
        <UserPlus className="h-5 w-5 text-gray-500" /> Add someone to this event
      </h3>
      <p className="mb-4 text-sm text-gray-600">
        For people who did not register themselves. Search for an existing account, or type an
        email address to add somebody who has none.
      </p>

      <div className="flex gap-2">
        <input
          value={term}
          onChange={(e) => { setTerm(e.target.value); setResults(null); }}
          onKeyDown={(e) => { if (e.key === 'Enter') search(); }}
          placeholder="Name or email address"
          className="flex-1 rounded-md border border-gray-300 px-3 py-2"
        />
        <button
          type="button" onClick={search} disabled={busy || !term.trim()}
          className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          <Search className="h-4 w-4" /> Search
        </button>
      </div>

      {results !== null && (
        <div className="mt-4">
          {results.length > 0 ? (
            <ul className="divide-y divide-gray-100 rounded-md border border-gray-200">
              {results.map((u) => (
                <li key={u._id} className="flex items-center justify-between gap-3 px-3 py-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-gray-900">{u.name || u.email}</p>
                    <p className="truncate text-xs text-gray-500">{u.email} · {u.role}</p>
                  </div>
                  <button
                    type="button" onClick={() => add({ userId: u._id }, u.email)} disabled={busy}
                    className="shrink-0 rounded-md border border-blue-300 px-3 py-1.5 text-sm text-blue-700 hover:bg-blue-50 disabled:opacity-50"
                  >
                    Add
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="rounded-md bg-gray-50 p-3 text-sm text-gray-600">No account matches that.</p>
          )}

          {looksLikeEmail && !results.some((u) => u.email.toLowerCase() === term.trim().toLowerCase()) && (
            <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3">
              <p className="mb-2 text-sm text-amber-900">
                Nobody holds <strong>{term.trim()}</strong>. You can still add them, but without an
                account they cannot be put in a team until they create one.
              </p>
              <button
                type="button" disabled={busy}
                onClick={() => add({ email: term.trim(), firstName: 'Added', lastName: 'by admin' }, term.trim())}
                className="rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50"
              >
                Add {term.trim()} anyway
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
